"use client";

import React, { FunctionComponent, useState } from "react";
import { animated, useSpring } from "@react-spring/web";

import { IField } from "@/types/hubspot";
import { validateField } from "@/helpers/formValidation";
import FormFieldError from "../form/fieldError";
import { Spinner } from "../spinner";

const NewsLetterForm: FunctionComponent = () => {
  const [email, setEmail] = useState<IField>({
    objectTypeId: "",
    name: "email",
    label: "Email",
    required: true,
    hidden: false,
    fieldType: "email",
    value: "",
    error: "",
  });
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isComplete, setCompleted] = useState(false);
  const { display, height, opacity, scale } = useSpring({
    display: isComplete ? "" : "hidden",
    height: isComplete ? "auto" : "0px",
    opacity: isComplete ? 1 : 0,
    scale: isComplete ? 1 : 0,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let field = { ...email };
    let submission = {
      fields: { email: email.value },
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };
    field.error = validateField(email);

    if (field.error) {
      setError(field.error);
      setEmail(field);
    } else {
      setSubmitting(true);
      fetch(`/api/form/${process.env.NEXT_PUBLIC_NEWSLETTER_FORM}`, {
        method: "post",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(submission),
      })
        .then((res) => {
          if (!res.ok) throw res;
          return res.json();
        })
        .then(() => {
          setSubmitting(false);
          setCompleted(true);
        })
        .catch((error) => {
          setSubmitting(false);
          error.json().then((body: any) => {
            field.error = body.error;
            setEmail(field);
          });
        });
    }
  };

  const handleUpdate = (value: string) => {
    let field = { ...email, error: "" };
    field.value = value;
    setEmail(field);
  };

  return (
    <div className="grow rounded-lg bg-white p-3 text-left md:max-w-lg xl:max-w-xl xl:p-10">
      <div>
        <form className="flex flex-col" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-3 xl:gap-6">
            <div className="flex flex-col gap-2">
              <span className="text-base font-bold lg:text-xl xl:text-2xl">
                Subscribe to our newsletter
              </span>
              <span className="">
                Stay up-to-date with the latest in sponsorship
              </span>
            </div>
            <div className="flex flex-col transition-height">
              <animated.div style={{ display: isComplete ? "none" : "" }}>
                <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:items-center lg:gap-0 xl:w-full">
                  <input
                    id="footer-newsletter"
                    className={`h-11 ${email.error ? "border-red-500 bg-red-50 focus:border-red-500" : "bg-white"} focus:ring-none flex-none appearance-none rounded-lg border border-gray-300 p-2 text-gray-900 focus:ring-0 lg:w-48 lg:flex-1 lg:rounded-r-none xl:w-96 xl:w-auto xl:max-w-none`}
                    type="email"
                    placeholder="Enter your email"
                    onChange={(event) => handleUpdate(event.target.value)}
                  />
                  <button className="h-11 w-full rounded-lg bg-primary p-3 text-sm font-medium text-white hover:bg-dark-primary focus:ring-0 lg:w-28 lg:rounded-l-none">
                    {isSubmitting ? (
                      <div className="inline-block h-4 w-4">
                        <Spinner />
                      </div>
                    ) : (
                      "Subscribe"
                    )}
                  </button>
                </div>
                {email.error && <FormFieldError field={email} error={error} />}
              </animated.div>
              <animated.div
                className=""
                style={{
                  display,
                  height,
                  opacity,
                  scale,
                }}
              >
                <div className="w-full text-center text-base font-bold text-gray-900">
                  You&apos;re subscribed!
                </div>
              </animated.div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsLetterForm;
